import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack, Text } from '@atlaskit/primitives';

import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from './providers/AiBulkNavigationContextProvider';
import { BulkConfigureFooter } from './BulkConfigureFooter';
import { useBulkCreateContext } from './providers/BulkCreateContextProvider';
import { BulkCreateNavigationHeader } from './BulkCreateNavigationHeader';
import { messages } from './messages';
import { SiteSelector } from './SiteSelector';
import { ProjectSelector } from './ProjectSelector';
import { DefaultIssueTypeSelector } from './DefaultIssueTypeSelector';
import { SidePanelWrapper } from './SidePanelWrapper';
import {
	useAvailableSitesState,
	useIsConfigureLoading,
	useIssueTypesState,
	useProjectsState,
	useResetConfigureState,
} from './providers/BulkConfigureContextProvider';

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkConfigureView = () => {
	const [
		{ currentJiraSite, currentJiraProject, currentDefaultIssueType },
		{ setCurrentJiraSite, setCurrentJiraProject, setCurrentDefaultIssueType },
	] = useBulkCreateContext();

	const { selectedAvailableSite } = useAvailableSitesState();
	const { selectedProject } = useProjectsState();
	const { selectedIssueType } = useIssueTypesState();
	const { isLoading } = useIsConfigureLoading();
	const resetConfigureState = useResetConfigureState();

	const goToPage = useAiBulkNavigationGotoPage();
	const currentPage = useAiBulkNavigationCurrentPage();

	const isOpen = currentPage === 'configure';

	const onSubmit = useCallback(() => {
		selectedAvailableSite && void setCurrentJiraSite(selectedAvailableSite);
		selectedProject && void setCurrentJiraProject(selectedProject);
		selectedIssueType && void setCurrentDefaultIssueType(selectedIssueType);
		void goToPage('default');
	}, [
		selectedAvailableSite,
		setCurrentJiraSite,
		selectedProject,
		setCurrentJiraProject,
		selectedIssueType,
		setCurrentDefaultIssueType,
		goToPage,
	]);

	const onClose = useCallback(() => {
		if (currentJiraSite && currentJiraProject && currentDefaultIssueType) {
			void resetConfigureState(currentJiraSite, currentJiraProject, currentDefaultIssueType);
		}
		void goToPage('default');
	}, [currentDefaultIssueType, currentJiraProject, currentJiraSite, goToPage, resetConfigureState]);

	return (
		<SidePanelWrapper isOpen={isOpen}>
			<Box padding="space.200">
				<Stack space="space.100">
					<BulkCreateNavigationHeader onClose={onClose}>
						<FormattedMessage {...messages.bulkConfigureHeaderText} />
					</BulkCreateNavigationHeader>
					<Text color="color.text.subtlest">
						<FormattedMessage {...messages.bulkCreateConfigureBodyText} />
					</Text>
					<Form onSubmit={onSubmit}>
						{({ formProps }) => (
							<form id="configureForm" {...formProps}>
								<SiteSelector />
								<ProjectSelector />
								<DefaultIssueTypeSelector />
							</form>
						)}
					</Form>
				</Stack>
			</Box>
			<BulkConfigureFooter onClose={onClose} isSaveDisabled={isLoading} />
		</SidePanelWrapper>
	);
};
